import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { Firebase } from "../../../../services/Firebase";
import { IOriginCountry } from "../../../../hooks/originCountries";
import { useAddOriginCountry } from "../../../../hooks/originCountries/useAddOriginCountry";
import { useModifyOriginCountry } from "../../../../hooks/originCountries/useModifyOriginCountry";
import { useDeleteOriginCountry } from "../../../../hooks/originCountries/useDeleteOriginCountry";
import { AttributeDialog } from "../../AttributeDialog";
import { CountriesDesktop } from "../../countries/CountriesDesktop";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyOriginCountryDialog: FunctionComponent<IModifyOriginCountryDialogProps> = ({
  open,
  onClose,
  originCountry,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = originCountry
    ? strings.origin_country_title
    : strings.new_origin_country_title;
  const { addOriginCountry } = useAddOriginCountry();
  const { modifyOriginCountry } = useModifyOriginCountry();
  const { deleteOriginCountry } = useDeleteOriginCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isCountryDesktopOpen, setIsCountryDesktopOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: originCountry?.name ?? "",
      country: originCountry?.country
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async ({ name, country }) => {
      setIsLoading(true);
      if (originCountry) {
        const { status, body } = await modifyOriginCountry({
          uuid: originCountry.uuid,
          name: name,
          countryIsoCode: country?.isoCode
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_origin_country_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addOriginCountry({
          name: name,
          countryIsoCode: country?.isoCode
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_origin_country_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!originCountry) return;
    setIsLoading(true);
    const { status, body } = await deleteOriginCountry({ uuid: originCountry.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(originCountry);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {originCountry && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.name_title}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.origin_country_flag_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="flag"
              name="flag"
              size="small"
              readOnly
              placeholder={strings.origin_country_flag_title}
              value={
                formik.values.country
                  ? `${formik.values.country?.name} ${formik.values.country?.emoji}`
                  : ""
              }
              onChange={formik.handleChange}
              onClick={() => setIsCountryDesktopOpen(true)}
              enterAction={() => setIsCountryDesktopOpen(true)}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AttributeDialog open={isCountryDesktopOpen} onClose={() => setIsCountryDesktopOpen(false)}>
        <CountriesDesktop
          handleClose={() => setIsCountryDesktopOpen(false)}
          columnWidth={isMobile ? 6 : 3}
          preselectedIsoCode={formik.values.country?.isoCode}
          onCountrySelected={async country => {
            setIsCountryDesktopOpen(false);
            await formik.setFieldValue("country", country);
          }}
          onClearValue={async () => {
            setIsCountryDesktopOpen(false);
            await formik.setFieldValue("country", null);
          }}
        />
      </AttributeDialog>
    </FormikProvider>
  );
};

export interface IModifyOriginCountryDialogProps {
  open: boolean;
  onClose: () => void;
  originCountry?: IOriginCountry;
  onNewItem?: (item: IOriginCountry) => void;
  onModifiedItem?: (item: IOriginCountry) => void;
  onDeletedItem?: (item: IOriginCountry) => void;
}
